import { useEffect, useState, useMemo } from "react";
import { toast } from "sonner";
import {
  Plus, Loader2, ShieldCheck, ShieldAlert, Check, X, Send, Trash2,
  FileText, Image as ImageIcon, Video, Mic, Filter, RefreshCw, Eye,
  Sparkles, AlertTriangle, ClipboardList, ChevronRight, Wand2, FileCode, Cpu,
} from "lucide-react";
import { api } from "@/lib/api";
import { formatApiError } from "@/contexts/AuthContext";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";

const STATUS_META = {
  draft:          { label: "TASLAK",         cls: "bg-neutral-900 text-neutral-300 border-neutral-700" },
  safety_check:   { label: "GÜVENLİK ⚠",    cls: "bg-amber-950/60 text-amber-300 border-amber-800" },
  pending_review: { label: "İNCELEME BEKLİYOR", cls: "bg-blue-950/60 text-blue-300 border-blue-800" },
  approved:       { label: "ONAYLI",         cls: "bg-emerald-950/60 text-emerald-300 border-emerald-800" },
  rejected:       { label: "REDDEDİLDİ",     cls: "bg-red-950/60 text-red-300 border-red-800" },
  published:      { label: "YAYINDA",        cls: "bg-red-600 text-white border-red-600" },
};

const TYPE_ICONS = {
  article: FileText,
  image: ImageIcon,
  video: Video,
  audio: Mic,
};

const CATEGORIES = ["technology", "science", "education", "life", "humor", "general", "history", "future", "anatomy"];
const TONES = ["neutral", "educational", "humorous", "formal", "casual", "child_friendly"];
const LENGTHS = ["short", "medium", "long"];
const CONTENT_TYPES = ["article", "image", "video", "audio"];

function StatCard({ label, value, tone = "neutral" }) {
  const cls = {
    neutral: "text-white",
    amber:   "text-amber-300",
    blue:    "text-blue-300",
    green:   "text-emerald-300",
    red:     "text-red-300",
  }[tone] || "text-white";
  return (
    <div className="bg-neutral-950 border border-neutral-800 rounded-sm p-4" data-testid={`content-stat-${label}`}>
      <p className="text-[10px] font-mono uppercase tracking-wider text-neutral-500">{label}</p>
      <p className={`headline text-3xl mt-1 ${cls}`}>{value ?? "—"}</p>
    </div>
  );
}

function StatusBadge({ status }) {  const m = STATUS_META[status] || STATUS_META.draft;
  return (
    <span className={`inline-block px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded-sm border ${m.cls}`}>
      {m.label}
    </span>
  );
}

const VIS_META = {
  private:            { label: "PRIVATE",     cls: "bg-neutral-900 border-neutral-700 text-neutral-400", icon: "🔒" },
  general:            { label: "GENERAL",     cls: "bg-emerald-950/60 border-emerald-800 text-emerald-300", icon: "🌐" },
  customer_exclusive: { label: "EXCLUSIVE",   cls: "bg-blue-950/60 border-blue-800 text-blue-300", icon: "👤" },
  plan_restricted:    { label: "PLAN",        cls: "bg-purple-950/60 border-purple-800 text-purple-300", icon: "⭐" },
  missing:            { label: "GÖRÜNÜRLÜK YOK", cls: "bg-yellow-950/60 border-yellow-800 text-yellow-300", icon: "⚠" },
};

function VisibilityBadge({ value }) {
  const key = value || "missing";
  const m = VIS_META[key] || VIS_META.missing;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded-sm border ${m.cls}`} data-testid={`visibility-badge-${key}`}>
      <span>{m.icon}</span> {m.label}
    </span>
  );
}

function CreateBriefDialog({ open, onOpenChange, onCreated }) {
  const empty = {
    topic: "", title: "", category: "general", language: "tr", content_type: "article",
    tone: "educational", target_audience: "", length: "medium",
    keywords: "", editorial_notes: "", body: "", summary: "",
    meta_title: "", meta_description: "", slug: "",
  };
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open) setForm(empty); /* eslint-disable-next-line */ }, [open]);

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        ...form,
        keywords: form.keywords.split(",").map((s) => s.trim()).filter(Boolean),
      };
      const { data } = await api.post("/admin/content", payload);
      toast.success("Brief oluşturuldu: " + data.id);
      onOpenChange(false);
      onCreated?.(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  const inputCls = "mt-1 w-full bg-neutral-900 border border-neutral-800 text-white rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-600";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="content-create-dialog">
        <DialogHeader>
          <DialogTitle className="headline text-2xl flex items-center gap-2">
            <Sparkles size={20} className="text-red-500" /> Yeni İçerik Brief&apos;i
          </DialogTitle>
          <DialogDescription>
            Loft Press operatörü olarak konu, başlık ve editoryal talimatları girin. Metni sonra düzenleyebilirsiniz.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="md:col-span-2">
              <label className="text-xs font-bold uppercase tracking-wider text-neutral-400">Ana Konu *</label>
              <input required value={form.topic} onChange={(e) => upd("topic", e.target.value)}
                     placeholder="ör. Bilgisayarlarda RAM nasıl çalışır?" className={inputCls} data-testid="brief-topic"/>
            </div>
            <div className="md:col-span-2">
              <label className="text-xs font-bold uppercase tracking-wider text-neutral-400">İçerik Başlığı *</label>
              <input required value={form.title} onChange={(e) => upd("title", e.target.value)}
                     className={inputCls} data-testid="brief-title"/>
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-neutral-400">Kategori</label>
              <select value={form.category} onChange={(e) => upd("category", e.target.value)} className={inputCls} data-testid="brief-category">
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-neutral-400">Dil</label>
              <select value={form.language} onChange={(e) => upd("language", e.target.value)} className={inputCls} data-testid="brief-language">
                <option value="tr">Türkçe</option>
                <option value="en">English</option>
              </select>
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-neutral-400">İçerik Türü</label>
              <select value={form.content_type} onChange={(e) => upd("content_type", e.target.value)} className={inputCls} data-testid="brief-type">
                {CONTENT_TYPES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-neutral-400">Anlatım Tonu</label>
              <select value={form.tone} onChange={(e) => upd("tone", e.target.value)} className={inputCls}>
                {TONES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-neutral-400">Uzunluk</label>
              <select value={form.length} onChange={(e) => upd("length", e.target.value)} className={inputCls}>
                {LENGTHS.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-neutral-400">Hedef Kitle</label>
              <input value={form.target_audience} onChange={(e) => upd("target_audience", e.target.value)}
                     placeholder="ör. yetişkin, öğrenci, geliştirici" className={inputCls}/>
            </div>
            <div className="md:col-span-2">
              <label className="text-xs font-bold uppercase tracking-wider text-neutral-400">Anahtar Kelimeler (virgülle)</label>
              <input value={form.keywords} onChange={(e) => upd("keywords", e.target.value)}
                     placeholder="RAM, bilgisayar belleği, DDR" className={inputCls}/>
            </div>
            <div className="md:col-span-2">
              <label className="text-xs font-bold uppercase tracking-wider text-neutral-400">Editoryal Talimatlar</label>
              <textarea rows={2} value={form.editorial_notes} onChange={(e) => upd("editorial_notes", e.target.value)}
                        placeholder="ör. konuşan bilgisayar mizahi karakter, teknik detayları basitleştir" className={inputCls}/>
            </div>

            <div className="md:col-span-2 border-t border-neutral-800 pt-3 mt-2">
              <p className="eyebrow text-neutral-500 mb-2">İÇERİK (opsiyonel — sonra da girilebilir)</p>
            </div>
            <div className="md:col-span-2">
              <label className="text-xs font-bold uppercase tracking-wider text-neutral-400">Özet</label>
              <textarea rows={2} value={form.summary} onChange={(e) => upd("summary", e.target.value)} className={inputCls}/>
            </div>
            <div className="md:col-span-2">
              <label className="text-xs font-bold uppercase tracking-wider text-neutral-400">Ana Metin</label>
              <textarea rows={6} value={form.body} onChange={(e) => upd("body", e.target.value)} className={inputCls} data-testid="brief-body"/>
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-neutral-400">Meta Başlık</label>
              <input value={form.meta_title} onChange={(e) => upd("meta_title", e.target.value)} className={inputCls}/>
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-neutral-400">URL Slug</label>
              <input value={form.slug} onChange={(e) => upd("slug", e.target.value)} placeholder="bilgisayarda-ram-nasil-calisir" className={inputCls}/>
            </div>
            <div className="md:col-span-2">
              <label className="text-xs font-bold uppercase tracking-wider text-neutral-400">Meta Açıklama</label>
              <textarea rows={2} value={form.meta_description} onChange={(e) => upd("meta_description", e.target.value)} className={inputCls}/>
            </div>
          </div>

          <div className="pt-3 flex justify-end gap-2">
            <button type="button" onClick={() => onOpenChange(false)}
                    className="px-4 py-2 border border-neutral-800 text-neutral-300 rounded-sm uppercase text-xs font-bold tracking-wider hover:bg-neutral-900">
              İptal
            </button>
            <button type="submit" disabled={busy}
                    className="px-5 py-2 bg-red-600 hover:bg-red-700 text-white rounded-sm uppercase text-xs font-bold tracking-wider inline-flex items-center gap-2 disabled:opacity-60"
                    data-testid="brief-submit">
              {busy ? <Loader2 size={14} className="animate-spin"/> : <Plus size={14}/>} Brief Oluştur
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function DetailDialog({ cid, open, onOpenChange, onChanged }) {
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState("");
  const [editBody, setEditBody] = useState("");
  const [editSummary, setEditSummary] = useState("");

  const load = async () => {
    if (!cid) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/content/${cid}`);
      setItem(data);
      setEditBody(data.body || "");
      setEditSummary(data.summary || "");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };

  useEffect(() => { if (open && cid) load(); /* eslint-disable-next-line */ }, [open, cid]);

  const doAction = async (action, opts = {}) => {
    setBusy(action);
    try {
      let res;
      if (action === "save") {
        res = await api.patch(`/admin/content/${cid}`, { body: editBody, summary: editSummary });
      } else if (action === "safety") {
        res = await api.post(`/admin/content/${cid}/safety-check`);
        toast.success(res.data.passed ? "Güvenlik: TEMİZ ✓" : `Güvenlik uyarı: ${(res.data.flags || []).join(", ")}`);
      } else if (action === "approve") {
        res = await api.post(`/admin/content/${cid}/approve`);
        toast.success("Onaylandı");
      } else if (action === "reject") {
        const reason = window.prompt("Red gerekçesi:");
        if (!reason) { setBusy(""); return; }
        res = await api.post(`/admin/content/${cid}/reject`, { reason });
        toast.success("Reddedildi");
      } else if (action === "publish") {
        res = await api.post(`/admin/content/${cid}/publish`);
        toast.success("Yayınlandı");
      } else if (action === "delete") {
        if (!window.confirm("Bu içeriği silmek istediğinize emin misiniz?")) { setBusy(""); return; }
        res = await api.delete(`/admin/content/${cid}`);
        toast.success("Silindi");
        onOpenChange(false);
        onChanged?.();
        setBusy("");
        return;
      }
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(""); }
  };

  const canEdit = item && item.status !== "published";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="content-detail-dialog">
        <DialogHeader>
          <DialogTitle className="headline text-2xl flex items-center gap-3">
            {item && <StatusBadge status={item.status}/>}
            <span className="truncate">{item?.title || (loading ? "Yükleniyor…" : "—")}</span>
          </DialogTitle>
          <DialogDescription className="font-mono text-xs">{cid}</DialogDescription>
        </DialogHeader>

        {loading || !item ? (
          <div className="py-12 flex justify-center"><Loader2 className="animate-spin text-red-500"/></div>
        ) : (
          <div className="space-y-5">
            {/* Brief info */}
            <div className="bg-neutral-950 border border-neutral-800 rounded-sm p-4 grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
              <div><p className="text-neutral-500 uppercase tracking-wider">Konu</p><p className="text-white mt-1">{item.topic}</p></div>
              <div><p className="text-neutral-500 uppercase tracking-wider">Kategori</p><p className="text-white mt-1">{item.category}</p></div>
              <div><p className="text-neutral-500 uppercase tracking-wider">Tür</p><p className="text-white mt-1">{item.content_type}</p></div>
              <div><p className="text-neutral-500 uppercase tracking-wider">Dil</p><p className="text-white mt-1">{item.language}</p></div>
              <div><p className="text-neutral-500 uppercase tracking-wider">Ton</p><p className="text-white mt-1">{item.tone || "—"}</p></div>
              <div><p className="text-neutral-500 uppercase tracking-wider">Uzunluk</p><p className="text-white mt-1">{item.length || "—"}</p></div>
              <div><p className="text-neutral-500 uppercase tracking-wider">Hedef</p><p className="text-white mt-1">{item.target_audience || "—"}</p></div>
              <div><p className="text-neutral-500 uppercase tracking-wider">Oluşturan</p><p className="text-white mt-1 font-mono">{item.created_by}</p></div>
              {item.keywords?.length > 0 && (
                <div className="col-span-2 md:col-span-4">
                  <p className="text-neutral-500 uppercase tracking-wider">Anahtar Kelimeler</p>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {item.keywords.map((k, i) => <span key={i} className="px-2 py-0.5 bg-neutral-900 border border-neutral-800 rounded-sm font-mono">{k}</span>)}
                  </div>
                </div>
              )}
              {item.editorial_notes && (
                <div className="col-span-2 md:col-span-4">
                  <p className="text-neutral-500 uppercase tracking-wider">Editoryal Talimat</p>
                  <p className="text-neutral-300 mt-1 leading-relaxed">{item.editorial_notes}</p>
                </div>
              )}
            </div>

            {/* Safety report */}
            {item.safety_report && (
              <div className={`rounded-sm p-4 border ${item.safety_flags?.length ? "bg-red-950/30 border-red-900" : "bg-emerald-950/30 border-emerald-900"}`}
                   data-testid="content-safety-report">
                <div className="flex items-center gap-2 mb-2">
                  {item.safety_flags?.length ? <ShieldAlert className="text-red-400" size={16}/> : <ShieldCheck className="text-emerald-400" size={16}/>}
                  <p className="eyebrow text-xs">{item.safety_flags?.length ? "GÜVENLİK UYARILARI" : "GÜVENLİK KONTROLÜ TEMİZ"}</p>
                </div>
                {item.safety_flags?.length > 0 && (
                  <div className="flex flex-wrap gap-1 mb-2">
                    {item.safety_flags.map((f) => (
                      <span key={f} className="px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded-sm bg-red-950 border border-red-800 text-red-300">{f}</span>
                    ))}
                  </div>
                )}
                {item.safety_report.llm?.reason && (
                  <p className="text-xs text-neutral-300 mt-1"><span className="text-neutral-500">LLM:</span> {item.safety_report.llm.reason}</p>
                )}
                {item.safety_report.keyword_flags?.length > 0 && (
                  <p className="text-xs text-neutral-300 mt-1"><span className="text-neutral-500">Blacklist:</span> {item.safety_report.keyword_flags.join(", ")}</p>
                )}
                <p className="text-[10px] font-mono text-neutral-600 mt-2">Kontrol: {item.safety_report.checked_at}</p>
              </div>
            )}

            {/* Reject reason */}
            {item.status === "rejected" && item.reject_reason && (
              <div className="rounded-sm p-4 bg-red-950/40 border border-red-900">
                <p className="eyebrow text-red-400">RED GEREKÇESİ</p>
                <p className="text-sm text-neutral-200 mt-1">{item.reject_reason}</p>
              </div>
            )}

            {/* Visibility panel (Faz 6) */}
            <VisibilityPanel item={item} onChanged={() => { load(); onChanged?.(); }}/>

            {/* Editable body */}
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-neutral-400 block mb-1">Özet</label>
              <textarea rows={2} disabled={!canEdit} value={editSummary} onChange={(e) => setEditSummary(e.target.value)}
                        className="w-full bg-neutral-900 border border-neutral-800 text-white rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-600 disabled:opacity-60"/>
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-neutral-400 block mb-1">Ana Metin</label>
              <textarea rows={12} disabled={!canEdit} value={editBody} onChange={(e) => setEditBody(e.target.value)}
                        className="w-full bg-neutral-900 border border-neutral-800 text-white rounded-sm px-3 py-2 text-sm font-mono leading-relaxed focus:outline-none focus:ring-2 focus:ring-red-600 disabled:opacity-60"
                        data-testid="content-body-editor"/>
              <p className="text-[10px] font-mono text-neutral-500 mt-1">
                {editBody.length.toLocaleString()} karakter · Body/başlık değişirse güvenlik durumu sıfırlanır.
              </p>
            </div>

            {/* Actions */}
            <div className="flex flex-wrap gap-2 pt-2 border-t border-neutral-800">
              {canEdit && (
                <button onClick={() => doAction("save")} disabled={busy}
                        className="px-4 py-2 bg-neutral-900 hover:bg-neutral-800 border border-neutral-800 text-white rounded-sm uppercase text-xs font-bold tracking-wider inline-flex items-center gap-1.5 disabled:opacity-60"
                        data-testid="content-save-btn">
                  {busy === "save" ? <Loader2 size={12} className="animate-spin"/> : <RefreshCw size={12}/>} Kaydet
                </button>
              )}
              {canEdit && (
                <button onClick={() => doAction("safety")} disabled={busy}
                        className="px-4 py-2 bg-amber-950/50 hover:bg-amber-900/50 border border-amber-800 text-amber-200 rounded-sm uppercase text-xs font-bold tracking-wider inline-flex items-center gap-1.5 disabled:opacity-60"
                        data-testid="content-safety-btn">
                  {busy === "safety" ? <Loader2 size={12} className="animate-spin"/> : <ShieldCheck size={12}/>} Güvenlik Kontrolü
                </button>
              )}
              {["pending_review", "safety_check", "draft"].includes(item.status) && (
                <button onClick={() => doAction("approve")} disabled={busy}
                        className="px-4 py-2 bg-emerald-950/50 hover:bg-emerald-900/50 border border-emerald-800 text-emerald-200 rounded-sm uppercase text-xs font-bold tracking-wider inline-flex items-center gap-1.5 disabled:opacity-60"
                        data-testid="content-approve-btn">
                  {busy === "approve" ? <Loader2 size={12} className="animate-spin"/> : <Check size={12}/>} Onayla
                </button>
              )}
              {item.status !== "rejected" && item.status !== "published" && (
                <button onClick={() => doAction("reject")} disabled={busy}
                        className="px-4 py-2 bg-red-950/50 hover:bg-red-900/50 border border-red-800 text-red-200 rounded-sm uppercase text-xs font-bold tracking-wider inline-flex items-center gap-1.5 disabled:opacity-60"
                        data-testid="content-reject-btn">
                  {busy === "reject" ? <Loader2 size={12} className="animate-spin"/> : <X size={12}/>} Reddet
                </button>
              )}
              {item.status === "approved" && (
                <button onClick={() => doAction("publish")} disabled={busy}
                        className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-sm uppercase text-xs font-bold tracking-wider inline-flex items-center gap-1.5 disabled:opacity-60"
                        data-testid="content-publish-btn">
                  {busy === "publish" ? <Loader2 size={12} className="animate-spin"/> : <Send size={12}/>} Yayınla
                </button>
              )}
              {canEdit && (
                <button onClick={() => doAction("delete")} disabled={busy}
                        className="ml-auto px-4 py-2 bg-neutral-900 hover:bg-red-950 border border-neutral-800 text-neutral-400 hover:text-red-300 rounded-sm uppercase text-xs font-bold tracking-wider inline-flex items-center gap-1.5 disabled:opacity-60"
                        data-testid="content-delete-btn">
                  {busy === "delete" ? <Loader2 size={12} className="animate-spin"/> : <Trash2 size={12}/>} Sil
                </button>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function AdminContentModule() {
  const [stats, setStats] = useState(null);
  const [items, setItems] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [visibilityFilter, setVisibilityFilter] = useState("");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [generateOpen, setGenerateOpen] = useState(false);
  const [detailId, setDetailId] = useState(null);
  const [subTab, setSubTab] = useState("content"); // "content" | "templates" | "audit"

  const load = async () => {
    setLoading(true);
    try {
      const [s, l] = await Promise.all([
        api.get("/admin/content/stats").then((r) => r.data),
        api.get("/admin/content", { params: { status: statusFilter || undefined, visibility: visibilityFilter || undefined, q: q || undefined, limit: 200 } }).then((r) => r.data),
      ]);
      setStats(s);
      setItems(l);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };

  useEffect(() => { if (subTab === "content") load(); /* eslint-disable-next-line */ }, [statusFilter, visibilityFilter, subTab]);

  const filterButtons = useMemo(() => [
    ["",               "Hepsi"],
    ["draft",          "Taslak"],
    ["safety_check",   "Güvenlik ⚠"],
    ["pending_review", "İnceleme"],
    ["approved",       "Onaylı"],
    ["rejected",       "Reddedilen"],
    ["published",      "Yayında"],
  ], []);

  return (
    <div data-testid="admin-content-module">
      {/* Business model banner */}
      <div className="bg-gradient-to-r from-red-950/40 via-neutral-950 to-neutral-950 border border-red-900/50 rounded-sm p-5 mb-6">
        <div className="flex items-start gap-3">
          <Sparkles className="text-red-500 flex-shrink-0 mt-0.5" size={22}/>
          <div className="flex-1">
            <p className="eyebrow text-red-500">LOFT PRESS · YENİ API SYSTEM</p>
            <h3 className="headline text-2xl text-white mt-1">Yapay zekâ destekli, insan onaylı editoryal içerik üretim hattı</h3>
            <p className="mt-2 text-sm text-neutral-400 leading-relaxed max-w-3xl">
              Operatör tarafından belirlenen konu ve başlıklardan yeni metin/görsel/ses/video içerikler üretilir; otomatik güvenlik kontrolünden ve manuel editoryal onaydan geçer; yalnızca onaylı ve yayına alınmış içerikler ileride API müşterilerine sunulur.
            </p>
            <p className="mt-2 text-[11px] font-mono uppercase tracking-wider text-neutral-500">
              draft → safety_check → pending_review → approved → published
            </p>
          </div>
        </div>
      </div>

      {/* Sub-tabs */}
      <div className="flex items-center gap-1 mb-5 border-b border-neutral-800">
        {[
          ["content", <FileText size={13} key="ic"/>, "İçerikler"],
          ["templates", <FileCode size={13} key="it"/>, "Prompt Şablonları"],
          ["audit", <Cpu size={13} key="ia"/>, "AI Üretim Kayıtları"],
        ].map(([key, icon, label]) => (
          <button
            key={key}
            onClick={() => setSubTab(key)}
            className={`px-4 py-2 text-xs font-bold uppercase tracking-wider inline-flex items-center gap-1.5 border-b-2 transition-colors ${
              subTab === key ? "border-red-600 text-white" : "border-transparent text-neutral-500 hover:text-white"
            }`}
            data-testid={`content-subtab-${key}`}
          >
            {icon} {label}
          </button>
        ))}
      </div>

      {subTab === "templates" && (<PromptTemplatesPanel/>)}
      {subTab === "audit" && (<AiAuditPanel/>)}
      {subTab === "content" && (<>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 mb-4">
        <StatCard label="TOPLAM"     value={stats?.total}/>
        <StatCard label="TASLAK"     value={stats?.by_status?.draft}/>
        <StatCard label="GÜVENLİK ⚠" value={stats?.by_status?.safety_check} tone="amber"/>
        <StatCard label="İNCELEME"   value={stats?.by_status?.pending_review} tone="blue"/>
        <StatCard label="ONAYLI"     value={stats?.by_status?.approved} tone="green"/>
        <StatCard label="REDDEDİLEN" value={stats?.by_status?.rejected} tone="red"/>
        <StatCard label="YAYINDA"    value={stats?.by_status?.published} tone="red"/>
      </div>

      {/* Visibility stats + legacy warning */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6" data-testid="visibility-stats">
          <StatCard label="🔒 PRIVATE"   value={stats?.by_visibility?.private}/>
          <StatCard label="🌐 GENERAL"   value={stats?.by_visibility?.general} tone="green"/>
          <StatCard label="👤 EXCLUSIVE" value={stats?.by_visibility?.customer_exclusive} tone="blue"/>
          <StatCard label="⭐ PLAN"      value={stats?.by_visibility?.plan_restricted}/>
          <StatCard label="⚠ GÖRÜNÜRLÜK YOK" value={stats?.legacy_visibility_missing} tone={stats?.legacy_visibility_missing > 0 ? "amber" : undefined}/>
        </div>
      )}
      {stats?.legacy_visibility_missing > 0 && (
        <div className="bg-yellow-950/30 border border-yellow-900 text-yellow-200 rounded-sm p-3 mb-4 text-xs flex items-start gap-2" data-testid="legacy-visibility-banner">
          <AlertTriangle size={14} className="flex-shrink-0 mt-0.5"/>
          <span>
            <strong>{stats.legacy_visibility_missing}</strong> içerikte görünürlük belirlenmemiş (eski kayıt). Paid API&apos;da <em>gizli</em> tutulur — güvenli varsayılan. Her birini açıp uygun görünürlüğü seçin. Filtre: <button onClick={() => setVisibilityFilter("missing")} className="underline hover:text-white">Görünürlük Yok</button>
          </span>
        </div>
      )}

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2 mb-2">
        <div className="flex items-center gap-1 flex-wrap">
          <Filter size={14} className="text-neutral-500 mr-1"/>
          {filterButtons.map(([val, label]) => (
            <button key={val || "all"} onClick={() => setStatusFilter(val)}
                    className={`px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider rounded-sm border transition-colors ${
                      statusFilter === val ? "bg-red-600 border-red-600 text-white"
                                           : "bg-neutral-950 border-neutral-800 text-neutral-400 hover:text-white hover:border-neutral-600"
                    }`}
                    data-testid={`content-filter-${val || "all"}`}>
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Visibility filter row */}
      <div className="flex flex-wrap items-center gap-1 mb-4">
        <span className="text-[10px] font-mono uppercase tracking-wider text-neutral-500 mr-2">Görünürlük:</span>
        {[["", "Hepsi"], ["missing", "Yok ⚠"], ["private", "Private"], ["general", "General"], ["customer_exclusive", "Exclusive"], ["plan_restricted", "Plan"]].map(([val, label]) => (
          <button key={val || "vis-all"} onClick={() => setVisibilityFilter(val)}
                  className={`px-2 py-1 text-[10px] font-bold uppercase tracking-wider rounded-sm border transition-colors ${
                    visibilityFilter === val ? "bg-blue-700 border-blue-600 text-white"
                                             : "bg-neutral-950 border-neutral-800 text-neutral-400 hover:text-white hover:border-neutral-600"
                  }`}
                  data-testid={`visibility-filter-${val || "all"}`}>
            {label}
          </button>
        ))}
        <div className="flex-1 flex items-center gap-2 min-w-[220px] ml-2">
          <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()}
                 placeholder="Başlık / konu ara…"
                 className="flex-1 bg-neutral-950 border border-neutral-800 text-white rounded-sm px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-red-600"
                 data-testid="content-search"/>
          <button onClick={load} className="p-2 border border-neutral-800 rounded-sm hover:bg-neutral-900 text-neutral-400"
                  data-testid="content-refresh"><RefreshCw size={14}/></button>
        </div>
        <button onClick={() => setGenerateOpen(true)}
                className="bg-gradient-to-r from-purple-600 to-red-600 hover:from-purple-500 hover:to-red-500 text-white px-4 py-2 rounded-sm uppercase text-xs font-bold tracking-wider inline-flex items-center gap-1.5"
                data-testid="content-ai-generate-btn">
          <Wand2 size={14}/> AI Üret
        </button>
        <button onClick={() => setCreateOpen(true)}
                className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-sm uppercase text-xs font-bold tracking-wider inline-flex items-center gap-1.5"
                data-testid="content-new-btn">
          <Plus size={14}/> Yeni Brief
        </button>
      </div>

      {/* Table */}
      <div className="bg-neutral-950 border border-neutral-800 rounded-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-neutral-900 text-neutral-400 text-[10px] uppercase tracking-wider">
            <tr>
              <th className="text-left px-4 py-3">Tür</th>
              <th className="text-left px-4 py-3">Başlık</th>
              <th className="text-left px-4 py-3">Kategori · Dil</th>
              <th className="text-left px-4 py-3">Durum</th>
              <th className="text-left px-4 py-3">Görünürlük</th>
              <th className="text-left px-4 py-3">Uyarılar</th>
              <th className="text-left px-4 py-3">Tarih</th>
              <th className="text-right px-4 py-3">Aç</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-800">
            {loading && items.length === 0 && (
              <tr><td colSpan="8" className="px-4 py-12 text-center"><Loader2 className="animate-spin text-red-500 inline"/></td></tr>
            )}
            {!loading && items.length === 0 && (
              <tr><td colSpan="8" className="px-4 py-16 text-center text-neutral-500" data-testid="content-empty">
                <ClipboardList size={28} className="mx-auto mb-2 text-neutral-700"/>
                Henüz içerik yok. Yeni bir brief oluşturun.
              </td></tr>
            )}
            {items.map((it) => {
              const Icon = TYPE_ICONS[it.content_type] || FileText;
              return (
                <tr key={it.id} className="hover:bg-neutral-900/50 cursor-pointer" onClick={() => setDetailId(it.id)}
                    data-testid={`content-row-${it.id}`}>
                  <td className="px-4 py-3"><Icon size={16} className="text-red-500"/></td>
                  <td className="px-4 py-3">
                    <p className="text-white font-medium truncate max-w-md">{it.title}</p>
                    <p className="text-[10px] font-mono text-neutral-500 truncate max-w-md">{it.topic}</p>
                  </td>
                  <td className="px-4 py-3 text-neutral-300">{it.category} · <span className="uppercase">{it.language}</span></td>
                  <td className="px-4 py-3"><StatusBadge status={it.status}/></td>
                  <td className="px-4 py-3"><VisibilityBadge value={it.api_visibility}/></td>
                  <td className="px-4 py-3">
                    {it.safety_flags?.length > 0 ? (
                      <span className="inline-flex items-center gap-1 text-[10px] text-red-300"><AlertTriangle size={12}/> {it.safety_flags.length}</span>
                    ) : it.safety_report ? (
                      <span className="inline-flex items-center gap-1 text-[10px] text-emerald-300"><ShieldCheck size={12}/> temiz</span>
                    ) : <span className="text-neutral-600 text-[10px]">—</span>}
                  </td>
                  <td className="px-4 py-3 font-mono text-neutral-500 text-xs">{new Date(it.created_at).toLocaleString("tr-TR")}</td>
                  <td className="px-4 py-3 text-right"><Eye size={14} className="text-neutral-500 inline"/><ChevronRight size={14} className="text-neutral-600 inline"/></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <CreateBriefDialog open={createOpen} onOpenChange={setCreateOpen} onCreated={() => load()}/>
      <GenerateAIDialog open={generateOpen} onOpenChange={setGenerateOpen} onGenerated={(res) => { if (res?.content?.id) setDetailId(res.content.id); load(); }}/>      <DetailDialog cid={detailId} open={!!detailId} onOpenChange={(v) => !v && setDetailId(null)} onChanged={load}/>
      </>)}
    </div>
  );
}

// ==================================================================
// AI Generation Dialog
// ==================================================================
// ── Async Generation Job constants ──────────────────────────────────────
const GEN_STEP_CONFIG = {
  queued:        { label: "İstek alındı",                progress: 10 },
  generating:    { label: "AI içerik oluşturuluyor…",   progress: 35 },
  safety_check:  { label: "Güvenlik kontrolü yapılıyor…", progress: 70 },
  saving:        { label: "İçerik kaydediliyor…",        progress: 90 },
  completed:     { label: "Tamamlandı",                   progress: 100 },
  failed:        { label: "Hata oluştu",                  progress: 0 },
};
const GEN_SESSION_KEY = "loftpress_active_gen";

// ==================================================================
// AI Generation Dialog — Async polling version
// ==================================================================
function GenerateAIDialog({ open, onOpenChange, onGenerated }) {
  const emptyForm = {
    topic: "", title: "", category: "general", language: "tr",
    tone: "educational", target_audience: "", length: "medium",
    keywords: "", editorial_notes: "",
    template_id: "", provider: "", model: "",
    auto_safety_check: true,
  };

  // Form state
  const [form, setForm] = useState(emptyForm);
  const [templates, setTemplates] = useState([]);
  const [config, setConfig] = useState(null);
  const [autofilling, setAutofilling] = useState(false);
  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Job state: "form" | "polling" | "completed" | "failed"
  const [phase, setPhase] = useState("form");
  const [genId, setGenId] = useState(null);
  const [jobStatus, setJobStatus] = useState(null);
  const [submitBusy, setSubmitBusy] = useState(false);

  // Load templates + config and restore any active job on open
  useEffect(() => {
    if (!open) return;
    Promise.all([
      api.get("/admin/prompt-templates").then((r) => r.data).catch(() => []),
      api.get("/admin/ai-generations/config").then((r) => r.data).catch(() => null),
    ]).then(([tpls, cfg]) => { setTemplates(tpls || []); setConfig(cfg); });

    // Restore in-progress job from sessionStorage
    const savedId = sessionStorage.getItem(GEN_SESSION_KEY);
    if (savedId && phase === "form") {
      api.get(`/admin/content/generations/${savedId}/status`)
        .then(({ data }) => {
          if (["queued", "generating", "safety_check", "saving"].includes(data.status)) {
            setGenId(savedId);
            setJobStatus(data);
            setPhase("polling");
          } else {
            sessionStorage.removeItem(GEN_SESSION_KEY);
          }
        })
        .catch(() => sessionStorage.removeItem(GEN_SESSION_KEY));
    }
    // eslint-disable-next-line
  }, [open]);

  // Polling loop
  useEffect(() => {
    if (phase !== "polling" || !genId) return;
    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      try {
        const { data } = await api.get(`/admin/content/generations/${genId}/status`);
        if (cancelled) return;
        setJobStatus(data);
        if (data.status === "completed") {
          setPhase("completed");
          sessionStorage.removeItem(GEN_SESSION_KEY);
          toast.success("İçerik başarıyla üretildi ve inceleme listesine eklendi.");
          onGenerated?.({ content: { id: data.content_id }, generation_id: genId });
        } else if (data.status === "failed") {
          setPhase("failed");
          sessionStorage.removeItem(GEN_SESSION_KEY);
        }
      } catch (e) {
        // Network glitch — keep polling silently
      }
    };
    poll();
    const interval = setInterval(poll, 3000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [phase, genId]); // eslint-disable-line

  const handleAutofill = async () => {
    if (!form.topic.trim()) return;
    setAutofilling(true);
    try {
      const { data } = await api.post("/admin/content/autofill-brief", {
        topic: form.topic.trim(),
        title: form.title.trim() || undefined,
        language: form.language,
      });
      setForm((f) => ({
        ...f,
        category: data.category || f.category,
        tone: data.tone || f.tone,
        length: data.length || f.length,
        target_audience: data.target_audience || f.target_audience,
        keywords: data.keywords || f.keywords,
        editorial_notes: data.editorial_notes || f.editorial_notes,
      }));
      toast.success("Form otomatik dolduruldu.");
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(detail ? formatApiError(detail) : "Otomatik doldurma başarısız oldu.");
    } finally { setAutofilling(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitBusy(true);
    try {
      const payload = {
        topic: form.topic,
        title: form.title || undefined,
        category: form.category,
        language: form.language,
        tone: form.tone,
        target_audience: form.target_audience || undefined,
        length: form.length,
        keywords: form.keywords.split(",").map((s) => s.trim()).filter(Boolean),
        editorial_notes: form.editorial_notes || undefined,
        template_id: form.template_id || undefined,
        provider: form.provider || undefined,
        model: form.model || undefined,
        auto_safety_check: !!form.auto_safety_check,
      };
      // 202 returns quickly — no long timeout needed
      const { data } = await api.post("/admin/content/generate", payload, { timeout: 15000 });
      const gid = data.generation_id;
      setGenId(gid);
      setJobStatus({ status: "queued", progress_percent: 10, message: data.message });
      setPhase("polling");
      sessionStorage.setItem(GEN_SESSION_KEY, gid);
      if (data.already_in_progress) {
        toast.info("Zaten devam eden bir üretim bulundu. Durumu takip ediliyor.");
      }
    } catch (err) {
      const isNetwork = !err.response;
      const detail = err.response?.data?.detail || err.response?.data?.message;
      if (isNetwork) {
        toast.error("Sunucuya ulaşılamadı. İnternet bağlantınızı kontrol edin.");
      } else {
        toast.error(detail ? formatApiError(detail) : "İstek gönderilemedi. Lütfen tekrar deneyin.");
      }
    } finally { setSubmitBusy(false); }
  };

  const handleClose = (newOpen) => {
    if (!newOpen && phase === "polling") {
      // Job continues in background — just dismiss dialog
      toast.info("Üretim arka planda devam ediyor. İçerik listesinden takip edebilirsiniz.");
      setPhase("form");
      setGenId(null);
      setJobStatus(null);
      setForm(emptyForm);
      onOpenChange(false);
      return;
    }
    if (!newOpen) {
      setPhase("form");
      setGenId(null);
      setJobStatus(null);
      setForm(emptyForm);
    }
    onOpenChange(newOpen);
  };

  const handleDone = () => {
    setPhase("form");
    setGenId(null);
    setJobStatus(null);
    setForm(emptyForm);
    onOpenChange(false);
  };

  const handleRetry = () => {
    setPhase("form");
    setGenId(null);
    setJobStatus(null);
    sessionStorage.removeItem(GEN_SESSION_KEY);
  };

  const inputCls = "mt-1 w-full bg-neutral-900 border border-neutral-800 text-white rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-600";
  const step = GEN_STEP_CONFIG[jobStatus?.status] || GEN_STEP_CONFIG.queued;
  const progressPct = jobStatus?.progress_percent ?? step.progress;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="ai-generate-dialog">
        <DialogHeader>
          <DialogTitle className="headline text-2xl flex items-center gap-2">
            <Wand2 className="text-red-500" size={22}/> AI İçerik Üret
          </DialogTitle>
          <DialogDescription>
            Brief verin, AI arka planda içeriği üretir — 504 timeout olmadan.
          </DialogDescription>
        </DialogHeader>

        {/* ── POLLING PHASE ── */}
        {phase === "polling" && (
          <div className="space-y-6 py-4" data-testid="ai-polling-view">
            <div className="space-y-3">
              <div className="flex justify-between text-xs font-mono text-neutral-400">
                <span data-testid="ai-step-label">{step.label}</span>
                <span>{progressPct}%</span>
              </div>
              <div className="w-full bg-neutral-900 rounded-full h-2 overflow-hidden">
                <div
                  className="bg-gradient-to-r from-purple-600 to-red-600 h-2 rounded-full transition-all duration-700"
                  style={{ width: `${progressPct}%` }}
                  data-testid="ai-progress-bar"
                />
              </div>
            </div>
            <div className="grid grid-cols-5 gap-1">
              {["queued","generating","safety_check","saving","completed"].map((s) => {
                const done = progressPct >= (GEN_STEP_CONFIG[s]?.progress || 0);
                return (
                  <div key={s} className={`text-center text-[9px] font-mono uppercase tracking-wider px-1 py-1.5 rounded-sm border transition-colors ${done ? "bg-red-950/40 border-red-800 text-red-300" : "bg-neutral-950 border-neutral-800 text-neutral-600"}`}>
                    {s.replace("_"," ")}
                  </div>
                );
              })}
            </div>
            {jobStatus?.message && (
              <p className="text-xs text-neutral-400 text-center">{jobStatus.message}</p>
            )}
            <div className="flex justify-center">
              <p className="text-[10px] font-mono text-neutral-600 flex items-center gap-1.5">
                <Loader2 size={10} className="animate-spin"/> ID: {genId}
              </p>
            </div>
            <div className="flex justify-center">
              <button
                onClick={handleClose.bind(null, false)}
                className="px-4 py-2 border border-neutral-800 text-neutral-400 rounded-sm text-xs font-bold uppercase tracking-wider hover:bg-neutral-900"
              >
                Arka Planda Devam Et
              </button>
            </div>
          </div>
        )}

        {/* ── COMPLETED PHASE ── */}
        {phase === "completed" && (
          <div className="space-y-4 py-4 text-center" data-testid="ai-completed-view">
            <div className="w-16 h-16 mx-auto rounded-full bg-emerald-950/40 border border-emerald-800 flex items-center justify-center">
              <Check size={28} className="text-emerald-400"/>
            </div>
            <div>
              <p className="headline text-xl text-white">İçerik Oluşturuldu</p>
              <p className="text-xs font-mono text-neutral-500 mt-1">{jobStatus?.content_id}</p>
            </div>
            <div className="flex gap-2 justify-center">
              <button
                onClick={handleDone}
                className="px-5 py-2 bg-red-600 hover:bg-red-700 text-white rounded-sm text-xs font-bold uppercase tracking-wider"
                data-testid="ai-done-btn"
              >
                İçeriği Görüntüle
              </button>
              <button
                onClick={handleRetry}
                className="px-4 py-2 border border-neutral-800 text-neutral-300 rounded-sm text-xs font-bold uppercase tracking-wider hover:bg-neutral-900"
              >
                Yeni Üretim
              </button>
            </div>
          </div>
        )}

        {/* ── FAILED PHASE ── */}
        {phase === "failed" && (
          <div className="space-y-4 py-4 text-center" data-testid="ai-failed-view">
            <div className="w-16 h-16 mx-auto rounded-full bg-red-950/40 border border-red-800 flex items-center justify-center">
              <X size={28} className="text-red-400"/>
            </div>
            <div>
              <p className="headline text-xl text-white">Üretim Başarısız</p>
              <p className="text-xs text-neutral-400 mt-1">{jobStatus?.message || "Bilinmeyen hata"}</p>
              {jobStatus?.error_code && (
                <p className="text-[10px] font-mono text-neutral-600 mt-0.5">{jobStatus.error_code}</p>
              )}
            </div>
            <div className="flex gap-2 justify-center">
              <button
                onClick={handleRetry}
                className="px-5 py-2 bg-red-600 hover:bg-red-700 text-white rounded-sm text-xs font-bold uppercase tracking-wider"
                data-testid="ai-retry-btn"
              >
                Yeniden Dene
              </button>
              <button
                onClick={handleDone}
                className="px-4 py-2 border border-neutral-800 text-neutral-300 rounded-sm text-xs font-bold uppercase tracking-wider hover:bg-neutral-900"
              >
                Kapat
              </button>
            </div>
          </div>
        )}

        {/* ── FORM PHASE ── */}
        {phase === "form" && (
          <>
            {config && (
              <div className="text-[11px] font-mono text-neutral-500 bg-neutral-950 border border-neutral-800 rounded-sm px-3 py-2 flex flex-wrap items-center gap-x-4 gap-y-1" data-testid="ai-config-banner">
                <span>MODEL: <span className="text-neutral-300">{config.provider}/{config.model}</span></span>
                <span>LIMIT: {config.rate_limit_per_min}/dk</span>
                <span>KEY: {config.llm_key_configured ? "✓" : "✗"}</span>
                <span className="text-emerald-500">ASYNC: 202 mod aktif</span>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="md:col-span-2">
                  <div className="flex items-center justify-between mb-1">
                    <label className="text-xs font-bold uppercase tracking-wider text-neutral-400">Ana Konu *</label>
                    <button
                      type="button"
                      onClick={handleAutofill}
                      disabled={!form.topic.trim() || autofilling || submitBusy}
                      className="inline-flex items-center gap-1.5 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider border border-purple-700 text-purple-400 hover:bg-purple-950/40 rounded-sm transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                      data-testid="ai-autofill-btn"
                    >
                      {autofilling
                        ? <Loader2 size={11} className="animate-spin"/>
                        : <Sparkles size={11}/>
                      }
                      {autofilling ? "Dolduruluyor…" : "Otomatik Doldur"}
                    </button>
                  </div>
                  <input required value={form.topic} onChange={(e) => upd("topic", e.target.value)}
                         placeholder="ör. Yapay zekanın günlük hayattaki 5 kullanımı" className={inputCls} data-testid="ai-topic"/>
                </div>
                <div className="md:col-span-2">
                  <label className="text-xs font-bold uppercase tracking-wider text-neutral-400">Başlık İpucu (opsiyonel)</label>
                  <input value={form.title} onChange={(e) => upd("title", e.target.value)}
                         placeholder="Boş bırakırsan AI üretir" className={inputCls} data-testid="ai-title"/>
                </div>
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-neutral-400">Kategori</label>
                  <select value={form.category} onChange={(e) => upd("category", e.target.value)} className={inputCls}>
                    {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-neutral-400">Dil</label>
                  <select value={form.language} onChange={(e) => upd("language", e.target.value)} className={inputCls}>
                    <option value="tr">Türkçe</option>
                    <option value="en">English</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-neutral-400">Ton</label>
                  <select value={form.tone} onChange={(e) => upd("tone", e.target.value)} className={inputCls}>
                    {TONES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-neutral-400">Uzunluk</label>
                  <select value={form.length} onChange={(e) => upd("length", e.target.value)} className={inputCls}>
                    {LENGTHS.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label className="text-xs font-bold uppercase tracking-wider text-neutral-400">Hedef Kitle</label>
                  <input value={form.target_audience} onChange={(e) => upd("target_audience", e.target.value)}
                         placeholder="ör. yetişkin, öğrenci, geliştirici" className={inputCls}/>
                </div>
                <div className="md:col-span-2">
                  <label className="text-xs font-bold uppercase tracking-wider text-neutral-400">Anahtar Kelimeler (virgülle)</label>
                  <input value={form.keywords} onChange={(e) => upd("keywords", e.target.value)}
                         placeholder="RAM, bilgisayar belleği, DDR" className={inputCls} data-testid="ai-keywords"/>
                </div>
                <div className="md:col-span-2">
                  <label className="text-xs font-bold uppercase tracking-wider text-neutral-400">Editoryal Talimatlar</label>
                  <textarea rows={2} value={form.editorial_notes} onChange={(e) => upd("editorial_notes", e.target.value)}
                            placeholder="ör. sade dil, mizahi karakter, kaynak referansı verme" className={inputCls}/>
                </div>
                <div className="md:col-span-2 border-t border-neutral-800 pt-3 mt-2">
                  <p className="eyebrow text-neutral-500 mb-2">GELİŞMİŞ (opsiyonel)</p>
                </div>
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-neutral-400">Prompt Şablonu</label>
                  <select value={form.template_id} onChange={(e) => upd("template_id", e.target.value)} className={inputCls} data-testid="ai-template-select">
                    <option value="">(varsayılan sistem promptu)</option>
                    {templates.map((t) => (
                      <option key={t.id} value={t.id}>{t.name} ({t.language})</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-neutral-400">Provider Override</label>
                  <select value={form.provider} onChange={(e) => upd("provider", e.target.value)} className={inputCls}>
                    <option value="">(env varsayılan)</option>
                    {(config?.allowed_providers || ["anthropic","openai","gemini"]).map((p) => (
                      <option key={p} value={p}>{p}</option>
                    ))}
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label className="text-xs font-bold uppercase tracking-wider text-neutral-400">Model Override</label>
                  <input value={form.model} onChange={(e) => upd("model", e.target.value)}
                         placeholder="Örn: claude-sonnet-4-6 / gpt-5.4 / gemini-3-flash-preview" className={inputCls}/>
                </div>
                <div className="md:col-span-2 flex items-center gap-2 pt-1">
                  <input id="ai-auto-safety" type="checkbox" checked={form.auto_safety_check}
                         onChange={(e) => upd("auto_safety_check", e.target.checked)}
                         className="accent-red-600" data-testid="ai-auto-safety"/>
                  <label htmlFor="ai-auto-safety" className="text-xs text-neutral-300">
                    Üretimden sonra otomatik güvenlik kontrolünü çalıştır (önerilir)
                  </label>
                </div>
              </div>
              <div className="pt-3 flex justify-end gap-2">
                <button type="button" onClick={() => onOpenChange(false)}
                        className="px-4 py-2 border border-neutral-800 text-neutral-300 rounded-sm uppercase text-xs font-bold tracking-wider hover:bg-neutral-900">
                  İptal
                </button>
                <button type="submit" disabled={submitBusy || !form.topic}
                        className="px-5 py-2 bg-gradient-to-r from-purple-600 to-red-600 hover:from-purple-500 hover:to-red-500 text-white rounded-sm uppercase text-xs font-bold tracking-wider inline-flex items-center gap-2 disabled:opacity-60"
                        data-testid="ai-submit">
                  {submitBusy ? <Loader2 size={14} className="animate-spin"/> : <Wand2 size={14}/>}
                  {submitBusy ? "Gönderiliyor…" : "AI ile Üret"}
                </button>
              </div>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ==================================================================
// Prompt Templates Panel (CRUD)
// ==================================================================
function PromptTemplatesPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null); // template being edited (or {} for new)

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/prompt-templates");
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm("Bu şablonu silmek istediğinize emin misiniz?")) return;
    try {
      await api.delete(`/admin/prompt-templates/${id}`);
      toast.success("Silindi");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <div data-testid="templates-panel">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="headline text-xl text-white">Prompt Şablonları</h3>
          <p className="text-xs text-neutral-500 mt-1">
            Yeniden kullanılabilir sistem + kullanıcı promptları. <code className="text-neutral-400">{"{{topic}}, {{category}}, {{keywords}}, {{length}}, {{tone}}, {{target_audience}}, {{editorial_notes}}, {{title}}"}</code> gibi değişkenler otomatik yerine geçer.
          </p>
        </div>
        <button onClick={() => setEditing({})}
                className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-sm uppercase text-xs font-bold tracking-wider inline-flex items-center gap-1.5"
                data-testid="template-new-btn">
          <Plus size={14}/> Yeni Şablon
        </button>
      </div>

      {loading ? (
        <div className="py-12 flex justify-center"><Loader2 className="animate-spin text-red-500"/></div>
      ) : items.length === 0 ? (
        <div className="py-16 text-center text-neutral-500 border border-dashed border-neutral-800 rounded-sm" data-testid="templates-empty">
          <FileCode size={28} className="mx-auto mb-2 text-neutral-700"/>
          Henüz şablon yok. Yeni bir tane oluşturun.
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {items.map((t) => (
            <div key={t.id} className="bg-neutral-950 border border-neutral-800 rounded-sm p-4" data-testid={`template-row-${t.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-white font-medium truncate">{t.name}</p>
                  <p className="text-[10px] font-mono text-neutral-500 mt-0.5">{t.id} · {t.language} · {t.default_length}/{t.default_tone}</p>
                </div>
                <div className="flex gap-1 flex-shrink-0">
                  <button onClick={() => setEditing(t)}
                          className="px-2 py-1 text-[10px] font-bold uppercase tracking-wider border border-neutral-800 text-neutral-300 hover:bg-neutral-900 rounded-sm"
                          data-testid={`template-edit-${t.id}`}>
                    Düzenle
                  </button>
                  <button onClick={() => remove(t.id)}
                          className="px-2 py-1 text-[10px] font-bold uppercase tracking-wider border border-neutral-800 text-neutral-400 hover:border-red-500 hover:text-red-400 rounded-sm"
                          data-testid={`template-delete-${t.id}`}>
                    Sil
                  </button>
                </div>
              </div>
              {t.description && <p className="text-xs text-neutral-400 mt-2">{t.description}</p>}
              <div className="mt-3 text-[10px] font-mono text-neutral-500 space-y-1">
                <p><span className="text-neutral-600">system:</span> {String(t.system_prompt).slice(0, 120)}{t.system_prompt?.length > 120 ? "…" : ""}</p>
                <p><span className="text-neutral-600">user:</span> {String(t.user_prompt_template).slice(0, 120)}{t.user_prompt_template?.length > 120 ? "…" : ""}</p>
                {t.variables?.length > 0 && (
                  <p><span className="text-neutral-600">vars:</span> {t.variables.join(", ")}</p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <TemplateEditDialog template={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }}/>
    </div>
  );
}

function TemplateEditDialog({ template, onClose, onSaved }) {
  const empty = {
    name: "", description: "", language: "tr",
    system_prompt: "", user_prompt_template: "",
    variables: "", default_length: "medium", default_tone: "educational",
  };
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const isEdit = template && template.id;

  useEffect(() => {
    if (!template) return;
    if (template.id) {
      setForm({
        ...empty, ...template,
        variables: (template.variables || []).join(", "),
      });
    } else {
      setForm(empty);
    }
    // eslint-disable-next-line
  }, [template]);

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        name: form.name,
        description: form.description || undefined,
        language: form.language,
        system_prompt: form.system_prompt,
        user_prompt_template: form.user_prompt_template,
        variables: form.variables.split(",").map((s) => s.trim()).filter(Boolean),
        default_length: form.default_length,
        default_tone: form.default_tone,
      };
      if (isEdit) {
        await api.patch(`/admin/prompt-templates/${template.id}`, payload);
        toast.success("Güncellendi");
      } else {
        await api.post("/admin/prompt-templates", payload);
        toast.success("Oluşturuldu");
      }
      onSaved?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  const inputCls = "mt-1 w-full bg-neutral-900 border border-neutral-800 text-white rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-600";

  return (
    <Dialog open={!!template} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="sm:max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="template-edit-dialog">
        <DialogHeader>
          <DialogTitle className="headline text-2xl flex items-center gap-2">
            <FileCode className="text-red-500" size={20}/> {isEdit ? "Şablonu Düzenle" : "Yeni Şablon"}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-neutral-400">Ad *</label>
              <input required value={form.name} onChange={(e) => upd("name", e.target.value)} className={inputCls} data-testid="template-name-input"/>
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-neutral-400">Dil</label>
              <select value={form.language} onChange={(e) => upd("language", e.target.value)} className={inputCls}>
                <option value="tr">Türkçe</option>
                <option value="en">English</option>
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="text-xs font-bold uppercase tracking-wider text-neutral-400">Açıklama</label>
              <input value={form.description} onChange={(e) => upd("description", e.target.value)} className={inputCls}/>
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-neutral-400">Varsayılan Uzunluk</label>
              <select value={form.default_length} onChange={(e) => upd("default_length", e.target.value)} className={inputCls}>
                {LENGTHS.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-neutral-400">Varsayılan Ton</label>
              <select value={form.default_tone} onChange={(e) => upd("default_tone", e.target.value)} className={inputCls}>
                {TONES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="text-xs font-bold uppercase tracking-wider text-neutral-400">System Prompt *</label>
              <textarea required rows={5} value={form.system_prompt} onChange={(e) => upd("system_prompt", e.target.value)}
                        className={`${inputCls} font-mono`} data-testid="template-system-input"
                        placeholder='Sen Loft Press için… JSON: {"title":"...","summary":"...","body":"...","meta_title":"...","meta_description":"...","slug":"..."}'/>
            </div>
            <div className="md:col-span-2">
              <label className="text-xs font-bold uppercase tracking-wider text-neutral-400">User Prompt Template *</label>
              <textarea required rows={5} value={form.user_prompt_template} onChange={(e) => upd("user_prompt_template", e.target.value)}
                        className={`${inputCls} font-mono`} data-testid="template-user-input"
                        placeholder="Konu: {{topic}}\nKategori: {{category}}\nAnahtar Kelimeler: {{keywords}}\nUzunluk: {{length}}\n\nJSON çıktısını üret."/>
            </div>
            <div className="md:col-span-2">
              <label className="text-xs font-bold uppercase tracking-wider text-neutral-400">Değişkenler (virgülle — UI ipucu için)</label>
              <input value={form.variables} onChange={(e) => upd("variables", e.target.value)}
                     placeholder="topic, category, keywords, length" className={inputCls}/>
            </div>
          </div>
          <div className="pt-3 flex justify-end gap-2">
            <button type="button" onClick={() => onClose?.()}
                    className="px-4 py-2 border border-neutral-800 text-neutral-300 rounded-sm uppercase text-xs font-bold tracking-wider hover:bg-neutral-900">
              İptal
            </button>
            <button type="submit" disabled={busy}
                    className="px-5 py-2 bg-red-600 hover:bg-red-700 text-white rounded-sm uppercase text-xs font-bold tracking-wider inline-flex items-center gap-2 disabled:opacity-60"
                    data-testid="template-submit">
              {busy ? <Loader2 size={14} className="animate-spin"/> : <Check size={14}/>} Kaydet
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ==================================================================
// AI Generation Audit Log Panel
// ==================================================================
function AiAuditPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/ai-generations", { params: { limit: 200 } });
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  return (
    <div data-testid="ai-audit-panel">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="headline text-xl text-white">AI Üretim Kayıtları</h3>
          <p className="text-xs text-neutral-500 mt-1">Her AI üretim için provider, model, süre ve brief.</p>
        </div>
        <button onClick={load} className="p-2 border border-neutral-800 rounded-sm hover:bg-neutral-900 text-neutral-400"
                data-testid="ai-audit-refresh"><RefreshCw size={14}/></button>
      </div>
      <div className="bg-neutral-950 border border-neutral-800 rounded-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-neutral-900 text-neutral-400 text-[10px] uppercase tracking-wider">
            <tr>
              <th className="text-left px-4 py-3">Tarih</th>
              <th className="text-left px-4 py-3">Admin</th>
              <th className="text-left px-4 py-3">Provider / Model</th>
              <th className="text-left px-4 py-3">Şablon</th>
              <th className="text-left px-4 py-3">Konu</th>
              <th className="text-right px-4 py-3">Süre</th>
              <th className="text-right px-4 py-3">Girdi/Çıktı</th>
              <th className="text-left px-4 py-3">İçerik ID</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-800">
            {loading && items.length === 0 && (
              <tr><td colSpan="8" className="px-4 py-12 text-center"><Loader2 className="animate-spin text-red-500 inline"/></td></tr>
            )}
            {!loading && items.length === 0 && (
              <tr><td colSpan="8" className="px-4 py-12 text-center text-neutral-500">Henüz AI üretim kaydı yok.</td></tr>
            )}
            {items.map((r) => (
              <tr key={r.id} className="hover:bg-neutral-900/50" data-testid={`ai-audit-row-${r.id}`}>
                <td className="px-4 py-3 font-mono text-xs text-neutral-400">{new Date(r.created_at).toLocaleString("tr-TR")}</td>
                <td className="px-4 py-3 text-neutral-300 text-xs">{r.admin_email}</td>
                <td className="px-4 py-3 text-white text-xs">{r.provider} / {r.model}</td>
                <td className="px-4 py-3 text-neutral-300 text-xs">{r.template_name || "—"}</td>
                <td className="px-4 py-3 text-neutral-200 text-xs truncate max-w-xs">{r.brief?.topic}</td>
                <td className="px-4 py-3 font-mono text-xs text-neutral-400 text-right">{r.duration_ms ? `${(r.duration_ms/1000).toFixed(1)}s` : "—"}</td>
                <td className="px-4 py-3 font-mono text-xs text-neutral-500 text-right">{r.input_chars || 0} / {r.output_chars || 0}</td>
                <td className="px-4 py-3 font-mono text-[10px] text-neutral-500">{r.content_id}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ==================================================================
// Visibility Panel (Faz 6 — customer isolation)
// ==================================================================
function VisibilityPanel({ item, onChanged }) {
  const current = item.api_visibility || "missing";
  const isLegacy = current === "missing";
  const [choice, setChoice] = useState(isLegacy ? "private" : current);
  const [selectedCustomers, setSelectedCustomers] = useState(
    (item.exclusive_customer_ids || []).map((id) => ({ id, email: "", name: "", loading: true }))
  );
  const [selectedPlans, setSelectedPlans] = useState(item.allowed_plans || []);
  const [customerSearch, setCustomerSearch] = useState("");
  const [customerResults, setCustomerResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [saving, setSaving] = useState(false);

  // Hydrate real emails for pre-selected customers on mount
  useEffect(() => {
    const ids = item.exclusive_customer_ids || [];
    if (ids.length === 0) return;
    (async () => {
      const out = [];
      for (const id of ids) {
        try {
          const { data } = await api.get("/admin/users", { params: { search: id, limit: 1 } });
          out.push(data[0] ? { id, email: data[0].email, name: data[0].name || "" } : { id, email: id, name: "" });
        } catch {
          out.push({ id, email: id, name: "" });
        }
      }
      setSelectedCustomers(out);
    })();
    // eslint-disable-next-line
  }, [item.id]);

  const searchCustomers = async () => {
    if (customerSearch.trim().length < 2) { setCustomerResults([]); return; }
    setSearching(true);
    try {
      const { data } = await api.get("/admin/users", { params: { search: customerSearch.trim(), limit: 20 } });
      const chosen = new Set(selectedCustomers.map((c) => c.id));
      setCustomerResults(data.filter((u) => !chosen.has(u.id)));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setSearching(false); }
  };

  const toggleCustomer = (u) => {
    setSelectedCustomers((cur) => cur.find((c) => c.id === u.id) ? cur.filter((c) => c.id !== u.id) : [...cur, { id: u.id, email: u.email, name: u.name || "" }]);
    setCustomerResults((cur) => cur.filter((c) => c.id !== u.id));
  };

  const removeCustomer = (id) => {
    setSelectedCustomers((cur) => cur.filter((c) => c.id !== id));
  };

  const togglePlan = (p) => {
    setSelectedPlans((cur) => cur.includes(p) ? cur.filter((x) => x !== p) : [...cur, p]);
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { api_visibility: choice };
      if (choice === "customer_exclusive") payload.exclusive_customer_ids = selectedCustomers.map((c) => c.id);
      if (choice === "plan_restricted") payload.allowed_plans = selectedPlans;
      await api.patch(`/admin/content/${item.id}/visibility`, payload);
      toast.success("Görünürlük güncellendi");
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setSaving(false); }
  };

  const dirty =
    choice !== (isLegacy ? "" : current) ||
    (choice === "customer_exclusive" && JSON.stringify(selectedCustomers.map((c) => c.id).sort()) !== JSON.stringify([...(item.exclusive_customer_ids || [])].sort())) ||
    (choice === "plan_restricted" && JSON.stringify([...selectedPlans].sort()) !== JSON.stringify([...(item.allowed_plans || [])].sort()));

  const canSave = dirty && !saving &&
    (choice !== "customer_exclusive" || selectedCustomers.length > 0) &&
    (choice !== "plan_restricted" || selectedPlans.length > 0);

  return (
    <div className={`rounded-sm p-4 border ${isLegacy ? "bg-yellow-950/30 border-yellow-900" : "bg-neutral-950 border-neutral-800"}`} data-testid="visibility-panel">
      <div className="flex items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-2">
          {isLegacy ? <AlertTriangle size={16} className="text-yellow-400"/> : <ShieldCheck size={16} className="text-neutral-400"/>}
          <p className="eyebrow text-xs">GÖRÜNÜRLÜK · PAID API</p>
          <VisibilityBadge value={item.api_visibility}/>
        </div>
        {isLegacy && (
          <span className="text-[10px] font-mono uppercase tracking-wider text-yellow-300">güvenli varsayılan: gizli</span>
        )}
      </div>

      {isLegacy && (
        <p className="text-xs text-yellow-200 mb-3 leading-relaxed">
          Bu içerik eski bir kayıt — görünürlük alanı yok. Paid API tarafında görünmez. Uygun görünürlüğü seçip kaydettiğinde canlıya çıkar.
        </p>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-3">
        {[
          ["private", "🔒 Private", "Hiçbir müşteri göremez — dahili taslak"],
          ["general", "🌐 General", "Tüm ödemeli müşteriler görebilir"],
          ["customer_exclusive", "👤 Customer Exclusive", "Yalnızca seçili müşteriler"],
          ["plan_restricted", "⭐ Plan Restricted", "Belirli plan(lar)"],
        ].map(([val, label, desc]) => (
          <button
            key={val}
            type="button"
            onClick={() => setChoice(val)}
            className={`text-left p-3 rounded-sm border transition-colors ${
              choice === val
                ? "bg-blue-950/40 border-blue-700"
                : "bg-neutral-900 border-neutral-800 hover:border-neutral-600"
            }`}
            data-testid={`vis-choice-${val}`}
          >
            <p className="text-sm text-white font-medium">{label}</p>
            <p className="text-[11px] text-neutral-500 mt-0.5">{desc}</p>
          </button>
        ))}
      </div>

      {choice === "customer_exclusive" && (
        <div className="border-t border-neutral-800 pt-3 mt-2 space-y-2" data-testid="vis-customer-picker">
          <p className="text-[11px] font-bold uppercase tracking-wider text-neutral-400">Seçili Müşteriler ({selectedCustomers.length})</p>
          <div className="flex flex-wrap gap-1">
            {selectedCustomers.length === 0 && <p className="text-xs text-neutral-500">Henüz kimse seçili değil.</p>}
            {selectedCustomers.map((c) => (
              <span key={c.id} className="inline-flex items-center gap-1 px-2 py-1 bg-blue-950/50 border border-blue-800 text-blue-200 rounded-sm text-xs" data-testid={`vis-customer-chip-${c.id}`}>
                {c.loading ? <><Loader2 size={10} className="animate-spin"/> yükleniyor…</> : (c.email || c.id)}
                <button onClick={() => removeCustomer(c.id)} className="hover:text-white ml-1"><X size={11}/></button>
              </span>
            ))}
          </div>
          <div className="flex gap-1 items-center">
            <input
              value={customerSearch}
              onChange={(e) => setCustomerSearch(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), searchCustomers())}
              placeholder="E-posta veya ad ile müşteri ara (min. 2 harf)"
              className="flex-1 bg-neutral-900 border border-neutral-800 text-white rounded-sm px-3 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-blue-600"
              data-testid="vis-customer-search-input"
            />
            <button onClick={searchCustomers} disabled={searching || customerSearch.trim().length < 2}
                    className="px-3 py-1.5 border border-neutral-800 rounded-sm text-neutral-300 hover:bg-neutral-900 text-xs disabled:opacity-40"
                    data-testid="vis-customer-search-btn">
              {searching ? <Loader2 size={12} className="animate-spin"/> : "Ara"}
            </button>
          </div>
          {customerResults.length > 0 && (
            <div className="border border-neutral-800 rounded-sm max-h-40 overflow-y-auto divide-y divide-neutral-800">
              {customerResults.map((u) => (
                <button key={u.id} type="button" onClick={() => toggleCustomer(u)}
                        className="w-full text-left px-3 py-2 hover:bg-neutral-900 flex items-center justify-between"
                        data-testid={`vis-customer-result-${u.id}`}>
                  <div>
                    <p className="text-xs text-white">{u.email}</p>
                    <p className="text-[10px] font-mono text-neutral-500">{u.name || "—"} · {u.plan || "free"}</p>
                  </div>
                  <Plus size={14} className="text-blue-400"/>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {choice === "plan_restricted" && (
        <div className="border-t border-neutral-800 pt-3 mt-2 space-y-2" data-testid="vis-plan-picker">
          <p className="text-[11px] font-bold uppercase tracking-wider text-neutral-400">İzinli Planlar</p>
          <div className="flex flex-wrap gap-1">
            {["free", "pro", "enterprise"].map((p) => (
              <button key={p} type="button" onClick={() => togglePlan(p)}
                      className={`px-3 py-1.5 text-xs font-bold uppercase tracking-wider rounded-sm border transition-colors ${
                        selectedPlans.includes(p)
                          ? "bg-purple-700 border-purple-600 text-white"
                          : "bg-neutral-900 border-neutral-800 text-neutral-400 hover:text-white hover:border-neutral-600"
                      }`}
                      data-testid={`vis-plan-${p}`}>
                {p}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="flex items-center gap-2 mt-4 pt-3 border-t border-neutral-800">
        <button onClick={save} disabled={!canSave}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-sm uppercase text-xs font-bold tracking-wider inline-flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="vis-save-btn">
          {saving ? <Loader2 size={12} className="animate-spin"/> : <Check size={12}/>} Görünürlüğü Kaydet
        </button>
        {dirty && <span className="text-[10px] font-mono uppercase tracking-wider text-yellow-400">değişiklikler kaydedilmedi</span>}
      </div>
    </div>
  );
}

